import { Picture, Text, useViewport, Section } from '@farmersdog/corgi-x';
import { useMemo, useCallback, useState, useEffect } from 'react';
import type { RefCallback } from 'react';
import classNames from 'classnames';

import { useHasScrolled } from 'src/hooks/useHasScrolled';

import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { ExtendedText } from '../../../HomepageRedesign/components';
import VideoTriggerButton from '../../../Reviews/components/VideoTriggerButton';

import LeoMobilePng from './assets/leo-mobile.png';
import LeoDesktopPng from './assets/leo-desktop.png';
import BogartMobilePng from './assets/bogart-mobile.png';
import BogartDesktopPng from './assets/bogart-desktop.png';
import LeoMobileWebp from './assets/leo-mobile.webp';
import LeoDesktopWebp from './assets/leo-desktop.webp';
import BogartMobileWebp from './assets/bogart-mobile.webp';
import BogartDesktopWebp from './assets/bogart-desktop.webp';
import underline from './assets/underline.json';
import shortUnderline from './assets/short-underline.json';

import styles from './CaseStudies.module.css';

const CASE_STUDIES = [
  {
    name: 'Bogart',
    testimonial:
      '“Once we got on The Farmer’s Dog he just went from lethargic to bounding energy.”',
    assets: {
      desktop: [BogartDesktopPng, BogartDesktopWebp],
      mobile: [BogartMobilePng, BogartMobileWebp],
    },
    underlineAnimation: JSON.stringify(underline),
    wistiaIds: {
      mobile: 'vuyeobuqyg',
      desktop: 'gyte6brrst',
    },
  },
  {
    name: 'Leo',
    testimonial:
      '“Ever since we introduced Leo to The Farmer’s Dog, his quality of life has been forever changed.”',
    assets: {
      desktop: [LeoDesktopPng, LeoDesktopWebp],
      mobile: [LeoMobilePng, LeoMobileWebp],
    },
    underlineAnimation: JSON.stringify(shortUnderline),
    wistiaIds: {
      mobile: 'fptyjsuti9',
      desktop: '68ynbptqm4',
    },
  },
];

interface CaseStudyProps {
  name: string;
  testimonial: string;
  assets: { desktop: string[]; mobile: string[] };
  pictureAlignmentRight: boolean;
  isMobile: boolean;
  underlineAnimation: string;
  hasScrolled: boolean;
  wistiaIds: {
    mobile: string;
    desktop: string;
  };
}

function CaseStudy({
  name,
  testimonial,
  assets,
  pictureAlignmentRight,
  isMobile,
  underlineAnimation,
  hasScrolled,
  wistiaIds,
}: CaseStudyProps) {
  const [isAnimated, setIsAnimated] = useState(false);

  const intersectionObserver = useMemo(() => {
    // This component is server-rendered, and IntersectionObserver is not available on the server,
    // so we should only create it on the client to avoid throwing an error.
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsAnimated(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.2,
      }
    );

    return observer;
  }, []);

  const containerRef: RefCallback<HTMLElement> = useCallback(
    node => {
      if (node) {
        intersectionObserver?.observe(node);
      } else {
        intersectionObserver?.disconnect();
      }
    },
    [intersectionObserver]
  );

  useEffect(() => {
    return () => {
      intersectionObserver?.disconnect();
    };
  }, [intersectionObserver]);

  return (
    <div
      ref={containerRef}
      className={classNames(styles.caseStudyContainer, {
        [styles.right]: pictureAlignmentRight,
      })}
    >
      <div>
        <Picture
          alt={`${name} images`}
          sources={assets.desktop}
          className={styles.desktopImage}
        />
        <Picture
          alt={`${name} images`}
          sources={assets.mobile}
          className={styles.mobileImage}
        />
      </div>
      <div className={styles.textContainer}>
        <Text
          variant={isMobile ? 'heading-16' : 'heading-40'}
          as="div"
          color="kale-3"
          className={styles.title}
          bold
        >
          {isMobile ? (
            `Meet ${name}`
          ) : (
            <>
              Meet
              <span className={styles.underlineContainer}>
                {' '}
                {name}
                {hasScrolled && (
                  <DotLottieReact
                    data={underlineAnimation}
                    autoplay={isAnimated}
                    className={classNames(styles.underline, {
                      [styles.shortUnderline]: name === 'Leo',
                    })}
                  />
                )}
              </span>
            </>
          )}
        </Text>
        <ExtendedText
          variant={isMobile ? 'article-24' : 'article-28'}
          color="kale-3"
          className={styles.testimonial}
          align={isMobile ? 'center' : 'left'}
        >
          {testimonial}
        </ExtendedText>
        <VideoTriggerButton
          ctaText="See Full Story"
          wistiaId={isMobile ? wistiaIds.mobile : wistiaIds.desktop}
          moduleName="Case Studies"
          className={styles.videoTriggerButton}
          ctaStyles={styles.fullStoryButton}
        />
      </div>
    </div>
  );
}

export function CaseStudies() {
  const viewport = useViewport();
  const isMobile = !viewport.lg;
  const hasScrolled = useHasScrolled();

  const spacing = isMobile ? 'sm' : 'xl';

  return (
    <Section aria-label="Case Studies section" vSpacing={spacing}>
      <div className={styles.caseStudies}>
        {CASE_STUDIES.map((caseStudy, index) => {
          return (
            <CaseStudy
              key={index}
              pictureAlignmentRight={index % 2 === 0}
              isMobile={isMobile}
              hasScrolled={hasScrolled}
              {...caseStudy}
            />
          );
        })}
      </div>
    </Section>
  );
}
